import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, Camera, Users, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { NAV } from "./AdminPanelLayout";

/* Heritage Staff , team directory (Aug 2026). Photos show up beside chat
   messages in the Bumface task boards. Tab toggles control which admin
   tabs each person sees when they log in with their email. */

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const BERRY = "#6E1E3A";
const GOLD = "#C9A268";

export function resizePhoto(file, maxDim = 256) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error("Could not read that image."));
    img.src = URL.createObjectURL(file);
  });
}

export const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export function StaffAvatar({ person, size = 40 }) {
  const px = `${size}px`;
  if (person?.photo) {
    return <img src={person.photo} alt={person.name} className="rounded-full object-cover flex-shrink-0" style={{ width: px, height: px, border: `2px solid ${GOLD}` }} />;
  }
  return (
    <span className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
      style={{ width: px, height: px, backgroundColor: AUBERGINE, border: `2px solid ${GOLD}`, fontSize: size / 2.6 }}>
      {initials(person?.name)}
    </span>
  );
}

const BLANK = { name: "", role: "", email: "", phone: "", photo: null, tabs: [] };

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | staff id
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/console/staff").then((r) => setStaff(r.data.staff || [])).catch(() => toast.error("Couldn't load staff."));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(BLANK); setEditing("new"); };
  const startEdit = (s) => { setForm({ name: s.name, role: s.role || "", email: s.email || "", phone: s.phone || "", photo: s.photo || null, tabs: s.tabs || [] }); setEditing(s.id); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give them a name."); return; }
    setBusy(true);
    try {
      if (editing === "new") await api.post("/console/staff", form);
      else await api.patch(`/console/staff/${editing}`, form);
      toast.success(editing === "new" ? `${form.name} added to the team.` : "Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the staff list?`)) return;
    try { await api.delete(`/console/staff/${s.id}`); toast.success("Removed."); load(); }
    catch { toast.error("Couldn't remove."); }
  };

  if (!staff) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid="admin-staff" className="space-y-5" style={{ color: AUBERGINE }}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: BERRY }}>✦ The Team</div>
          <h1 className="text-4xl mt-1 leading-tight">Heritage Staff</h1>
          <p className="italic mt-1 text-sm opacity-70">
            Faces, roles and contact details. Photos appear beside chat notes, and the tab toggles decide what each person sees when they log in.
          </p>
        </div>
        <button type="button" onClick={startNew} data-testid="staff-add"
          className="h-10 px-5 rounded text-white text-sm inline-flex items-center gap-2" style={{ backgroundColor: BERRY }}>
          <Plus className="w-4 h-4" /> Add staff member
        </button>
      </header>

      {editing && (
        <StaffForm form={form} setForm={setForm} busy={busy} onSave={save} onCancel={() => setEditing(null)} isNew={editing === "new"} />
      )}

      {staff.length === 0 && !editing ? (
        <div className="bg-white border rounded-md p-10 text-center italic opacity-60" style={{ borderColor: EDGE }} data-testid="staff-empty">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-40" /> No staff yet. Add the first team member above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {staff.map((s) => (
            <div key={s.id} className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid={`staff-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <StaffAvatar person={s} size={56} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-lg leading-tight">{s.name}</div>
                  {s.role && <div className="text-[10px] tracking-[0.25em] uppercase font-semibold mt-0.5" style={{ color: GOLD }}>{s.role}</div>}
                  {s.email && <div className="text-xs opacity-70 mt-1 truncate">{s.email}</div>}
                  {s.phone && <div className="text-xs opacity-70">{s.phone}</div>}
                  <div className="text-[10px] opacity-50 mt-1.5">
                    {s.tabs?.length ? `Sees ${s.tabs.length} tab${s.tabs.length === 1 ? "" : "s"}` : "Sees every tab"}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => startEdit(s)} title="Edit" data-testid={`staff-edit-${s.id}`} className="p-1.5 rounded hover:bg-black/5"><Pencil className="w-4 h-4" /></button>
                  <button type="button" onClick={() => remove(s)} title="Remove" data-testid={`staff-delete-${s.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StaffForm({ form, setForm, busy, onSave, onCancel, isNew }) {
  const fileRef = useRef(null);
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { const p = await resizePhoto(file, 256); setForm((f) => ({ ...f, photo: p })); }
    catch { toast.error("Couldn't read that image."); }
  };
  const toggleTab = (to) =>
    setForm((f) => ({ ...f, tabs: f.tabs.includes(to) ? f.tabs.filter((t) => t !== to) : [...f.tabs, to] }));

  return (
    <div className="bg-white border-2 rounded-md p-5 space-y-4" style={{ borderColor: GOLD }} data-testid="staff-form">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: BERRY }}>
          {isNew ? "New staff member" : `Editing ${form.name || "staff member"}`}
        </div>
        <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-black/5" data-testid="staff-form-cancel"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-col sm:flex-row gap-5">
        <div className="flex flex-col items-center gap-2">
          <StaffAvatar person={form} size={88} />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="staff-photo-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] tracking-[0.15em] uppercase font-semibold"
            style={{ borderColor: AUBERGINE, color: AUBERGINE }}>
            <Camera className="w-3.5 h-3.5" /> {form.photo ? "Change" : "Add photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhoto} data-testid="staff-photo-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
          <Field label="Name" v={form.name} set={(v) => setForm({ ...form, name: v })} testid="staff-name" placeholder="Kayla Heritage" />
          <Field label="Role" v={form.role} set={(v) => setForm({ ...form, role: v })} testid="staff-role" placeholder="Director / Packing / Data entry" />
          <Field label="Email (their admin login)" v={form.email} set={(v) => setForm({ ...form, email: v })} testid="staff-email" placeholder="them@heritagedahlias.nz" />
          <Field label="Phone" v={form.phone} set={(v) => setForm({ ...form, phone: v })} testid="staff-phone" placeholder="021 …" />
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70 mb-1.5">
          Tabs they can see , leave all off to show everything
        </div>
        <div className="space-y-2.5">
          {NAV.map((group) => (
            <div key={group.group}>
              <div className="text-[9px] tracking-[0.3em] uppercase opacity-50 mb-1">{group.group}</div>
              <div className="flex flex-wrap gap-1.5">
                {group.items.map((item) => {
                  const on = form.tabs.includes(item.to);
                  return (
                    <button key={item.to} type="button" onClick={() => toggleTab(item.to)} data-testid={`staff-tab-${item.testid}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-semibold transition-colors"
                      style={on ? { backgroundColor: AUBERGINE, borderColor: AUBERGINE, color: "#fff" } : { borderColor: EDGE, color: AUBERGINE, backgroundColor: CREAM }}>
                      {on && <Check className="w-3 h-3" />} {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-10 px-4 rounded border text-sm" style={{ borderColor: EDGE }}>Cancel</button>
        <button type="button" onClick={onSave} disabled={busy} data-testid="staff-save"
          className="h-10 px-6 rounded text-white text-sm inline-flex items-center gap-2" style={{ backgroundColor: BERRY }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, set, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">{label}</label>
      <input value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} />
    </div>
  );
}
